'use client';

import type { ImageTextGridProps } from '@/core/types';

import React from 'react';

import { Box, Image, Container, Typography } from '@/components/ui';

type LocalProps = ImageTextGridProps & {
  className?: string;
};

export default function ImageTextGrid({
  title,
  description,
  items = [],
  className = '',
}: LocalProps) {
  // Ensure at least 6 elements
  const normalizedItems = [...items];
  while (normalizedItems.length < 6) {
    normalizedItems.push(null as any);
  }

  const renderCard = (
    item: (typeof items)[number] | null,
    large = false,
    key?: string,
    wrapperClass?: string
  ) => {
    if (!item) return null;
    return (
      <Box
        key={key}
        className={`${large ? 'col-span-2' : ''} flex flex-col relative ${wrapperClass || ''}`}
      >
        <Image
          src={item.image?.url || ''}
          alt={item.image?.alt || ''}
          className={`w-full rounded-[24px] ${large ? 'h-full object-cover' : ''}`}
        />
        {item.title && (
          <Typography
            variant="subtitle1"
            paragraph
            className="absolute z-10 text-white p-2 sm:p-4 md:px-6 md:py-4 sm:text-[18px] text-[12px] font-medium max-w-[360px] uppercase"
          >
            <span dangerouslySetInnerHTML={{ __html: item.title }} />
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box component="section" className={`relative ${className} py-16`}>
      <Container>
        {title && (
          <Typography
            variant="h2"
            className="uppercase text-[24px] md:text-[32px] font-bold mb-3 text-black"
          >
            {title}
          </Typography>
        )}

        {description && (
          <Box
            className="hidden md:block text-[16px] max-w-3xl mx-auto mb-8 lg:mb-[80px] [&>p]:mb-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-4"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}

        <Box className="grid lg:grid-cols-2 gap-3 md:gap-6">
          {/* First column */}
          <Box className="grid grid-cols-2 gap-3 md:gap-6">
            {renderCard(normalizedItems[0], true, 'big-0')}
            {normalizedItems
              .slice(1, 3)
              .map((item, idx) => renderCard(item, false, `small-1-${idx}`))}
          </Box>

          {/* Second column */}
          <Box className="grid md:grid-cols-2 gap-3 md:gap-6">
            {normalizedItems.slice(3, 5).map((item, idx) => {
              const orderClass = idx === 0 ? 'order-2 lg:order-1' : 'order-3 lg:order-2';
              return renderCard(item, false, `small-2-${idx}`, orderClass);
            })}
            {renderCard(normalizedItems[5], true, 'big-5', 'order-1 lg:order-3')}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
